// Países com DDI + locale do sistema. O país escolhido no seletor de telefone
// define também o locale (regra do bFocus). Brasil mascara; outros, só dígitos.
export interface Country {
  code: string;
  ddi: string;
  name: string;
  flag: string;
  locale: string;
}

export const COUNTRIES: Country[] = [
  { code: 'BR', ddi: '+55', name: 'Brasil', flag: '🇧🇷', locale: 'pt-BR' },
  { code: 'US', ddi: '+1', name: 'EUA / Canadá', flag: '🇺🇸', locale: 'en' },
  { code: 'PT', ddi: '+351', name: 'Portugal', flag: '🇵🇹', locale: 'pt-BR' },
  { code: 'AR', ddi: '+54', name: 'Argentina', flag: '🇦🇷', locale: 'es' },
  { code: 'UY', ddi: '+598', name: 'Uruguai', flag: '🇺🇾', locale: 'es' },
  { code: 'PY', ddi: '+595', name: 'Paraguai', flag: '🇵🇾', locale: 'es' },
  { code: 'CL', ddi: '+56', name: 'Chile', flag: '🇨🇱', locale: 'es' },
  { code: 'CO', ddi: '+57', name: 'Colômbia', flag: '🇨🇴', locale: 'es' },
  { code: 'MX', ddi: '+52', name: 'México', flag: '🇲🇽', locale: 'es' },
  { code: 'ES', ddi: '+34', name: 'Espanha', flag: '🇪🇸', locale: 'es' },
  { code: 'DE', ddi: '+49', name: 'Alemanha', flag: '🇩🇪', locale: 'de' },
  { code: 'FR', ddi: '+33', name: 'França', flag: '🇫🇷', locale: 'fr' },
  { code: 'GB', ddi: '+44', name: 'Reino Unido', flag: '🇬🇧', locale: 'en' },
  { code: 'IT', ddi: '+39', name: 'Itália', flag: '🇮🇹', locale: 'it' },
];

export const DEFAULT_COUNTRY = COUNTRIES[0];

export function onlyDigits(v: string): string {
  return v.replace(/\D/g, '');
}

// Máscara BR. Trata as DUAS formas: 8 dígitos locais (fixos → (11) 9999-9999) e
// 9 dígitos (celular → (11) 99999-9999).
export function applyBrMask(digits: string): string {
  const d = digits.slice(0, 11);
  if (d.length <= 2) return d.length ? `(${d}` : '';
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

/**
 * Completa o 9º dígito de CELULARES brasileiros que vieram sem ele (DDD + 8
 * dígitos cujo 1º dígito do assinante é 6–9 → mobile). Fixos (assinante começa
 * em 2–5) NÃO recebem o 9 — existem WhatsApp em números residenciais. `digits`
 * é DDD + assinante (sem DDI). Só altera quando dá para identificar como mobile.
 */
export function completeBrNinthDigit(digits: string): string {
  if (digits.length === 10 && /[6-9]/.test(digits[2])) {
    return digits.slice(0, 2) + '9' + digits.slice(2);
  }
  return digits;
}

/**
 * As duas formas de um celular brasileiro, com e sem o 9 da frente, em dígitos
 * com DDI. A Meta identifica o contato pelo wa_id, que para números antigos vem
 * SEM o 9; quem digita usa a forma atual. São o mesmo WhatsApp.
 */
export function brPhoneForms(value: string): string[] {
  const d = onlyDigits(value);
  if (d.startsWith('55') && d.length === 13 && d[4] === '9' && /[6-9]/.test(d[5])) return [d, d.slice(0, 4) + d.slice(5)];
  if (d.startsWith('55') && d.length === 12 && /[6-9]/.test(d[4])) return [d, d.slice(0, 4) + '9' + d.slice(4)];
  return [d];
}

/** Mesmo WhatsApp? Compara dígitos, aceitando o celular brasileiro com ou sem o 9. */
export function samePhone(a: string, b: string): boolean {
  const fa = brPhoneForms(a);
  return !!fa[0] && brPhoneForms(b).some((x) => fa.includes(x));
}

// E.164 plausível para os países suportados (BR chega a 13: +55 + 11). Acima
// disso é um id interno do WhatsApp (ex.: @lid), não um número discável.
const MAX_PHONE_DIGITS = 13;

/** Heurística: parece um telefone discável (e não um id interno do WhatsApp). */
export function looksLikePhone(value: string | null | undefined): boolean {
  const d = onlyDigits(value ?? '');
  return d.length >= 8 && d.length <= MAX_PHONE_DIGITS;
}

/**
 * Decide como exibir o identificador de uma conversa. Prefira passar o
 * `chat_jid` (com sufixo) — é o sinal confiável. Telefones reais recebem
 * máscara; grupos, LIDs e ids não discáveis voltam como id (sem fingir ser
 * telefone). `isPhone` permite ao chamador exibir o telefone só quando for real.
 */
export function formatChatIdentity(jidOrPhone: string | null | undefined): { text: string; isPhone: boolean } {
  const raw = (jidOrPhone ?? '').trim();
  if (!raw) return { text: '', isPhone: false };
  const at = raw.indexOf('@');
  const user = at >= 0 ? raw.slice(0, at) : raw;
  const suffix = at >= 0 ? raw.slice(at + 1) : '';
  if (suffix === 'g.us' || suffix === 'lid' || !looksLikePhone(user)) {
    return { text: user, isPhone: false };
  }
  return { text: formatPhone(user), isPhone: true };
}

export function detectCountry(phone: string): Country {
  if (!phone.startsWith('+')) return DEFAULT_COUNTRY;
  const sorted = [...COUNTRIES].sort((a, b) => b.ddi.length - a.ddi.length);
  return sorted.find((c) => phone.startsWith(c.ddi)) ?? DEFAULT_COUNTRY;
}

export function extractLocalDigits(phone: string, country: Country): string {
  if (phone.startsWith(country.ddi)) return onlyDigits(phone.slice(country.ddi.length));
  return onlyDigits(phone);
}

/**
 * Formata um telefone armazenado ("+5551999198087") para exibição, aplicando a
 * máscara do país quando houver (BR). Ex.: "+55 (51) 99919-8087" / "+1 4155551234".
 */
export function formatPhone(phone: string | null | undefined): string {
  if (!phone) return '';
  // O número pode chegar sem o "+" (ex.: parte de usuário de um JID do WhatsApp,
  // que é sempre o internacional completo). Normaliza para detectar o país e não
  // tratar o código do país (DDI) como se fosse o DDD.
  const normalized = phone.startsWith('+') ? phone : '+' + onlyDigits(phone);
  const country = detectCountry(normalized);
  let digits = extractLocalDigits(normalized, country);
  if (!digits) return phone;
  if (country.code === 'BR') digits = completeBrNinthDigit(digits);
  const local = country.code === 'BR' ? applyBrMask(digits) : digits;
  return `${country.ddi} ${local}`;
}
